import { StateStorage } from 'zustand/middleware'

// Absent outside a browser (the unit tests run in node). zustand's own
// storage then persists nothing rather than throwing, and so does this one.
const browserStorage = () =>
  typeof localStorage === 'undefined' ? null : localStorage

/*
  The venue this page belongs to, taken the first time one is open. Opening
  another always loads the page afresh, so it holds for the page's life. Read
  on every write instead, it followed `restaurantId`, which every tab shares:
  a tab still on one venue filed its saves under the venue another tab had
  just opened, and that venue's own data was lost the next time it loaded.
*/
let pageRestaurant: string | null = null

// Null while no venue has been open on this page: before the first login.
const scopedKey = (storage: Storage, name: string) => {
  pageRestaurant ??= storage.getItem('restaurantId')
  return pageRestaurant ? `${name}:${pageRestaurant}` : null
}

/**
 * localStorage with every key filed under the venue that is open.
 *
 * For the stores that keep a venue's own data in the browser while the
 * backend has no endpoint for it (floor plan, loyalty, review invites, the
 * website, the restaurant details). Unscoped, an owner of two venues who
 * switched to the second was shown the first one's tables, rules and NIP.
 * Opening a venue loads the page afresh (`switchRestaurant`, and
 * `enterRestaurant` at login), so each store reads that venue's copy when it is
 * created.
 *
 * Before a venue is open nothing is read or written. After one was, the page's
 * stores keep saving under it, even once a 401 has signed the browser out or
 * another tab has opened a different venue: what they hold is that venue's.
 *
 * Data saved before this existed belongs to the venue that was open then. It
 * moves under that venue the first time it is read, and the unscoped copy is
 * removed, so a second venue does not inherit it.
 */
export const restaurantScopedStorage: StateStorage = {
  getItem: name => {
    const storage = browserStorage()
    if (!storage) return null
    const key = scopedKey(storage, name)
    if (!key) return null
    const scoped = storage.getItem(key)
    if (scoped !== null) return scoped

    const legacy = storage.getItem(name)
    if (legacy !== null) {
      storage.setItem(key, legacy)
      storage.removeItem(name)
    }
    return legacy
  },
  setItem: (name, value) => {
    const storage = browserStorage()
    const key = storage && scopedKey(storage, name)
    if (key) storage.setItem(key, value)
  },
  removeItem: name => {
    const storage = browserStorage()
    const key = storage && scopedKey(storage, name)
    if (key) storage.removeItem(key)
  }
}

export default restaurantScopedStorage
